/**
 * ProfileSettings Component (Refactored)
 *
 * Settings page for user profile management
 * Supports both Client and Contractor modes
 * Based on Figma design: node-id=1-1448
 *
 * This component has been refactored to improve:
 * - Readability: Separated into smaller, focused components
 * - Maintainability: Logic extracted into custom hooks
 * - Scalability: Modular structure allows easy extension
 * - Reusability: UI components can be used elsewhere
 */

'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';
import {
  useMyContractorProfile,
  useUpdateContractorProfile,
  useUpdateContractorLocation,
  useUpdateUserProfile,
  useUpdateActorProfile,
  useUploadAvatar,
  useMyPortfolio,
  useDeletePortfolioItem,
  useCategories,
  useMyCategories,
  useAssignCategories,
  useMyServices,
  useCreateService,
  useUpdateService,
  useDeleteService,
  useMyWorkingHours,
  useUpdateWorkingHours,
  useCreatePortfolioItem,
  // Client profile hooks
  useMyClientProfile,
  useUpdateClientProfile,
  useUpdateClientLocation,
} from '../model/hooks';
import { uploadGalleryImage } from '../api';
import { Spinner } from '@/src/shared/ui/spinner/Spinner';
import { ClientProfileForm } from './ProfileSettings/ClientProfileForm';
import { ContractorProfileForm } from './ProfileSettings/ContractorProfileForm';
import { AccountSection } from './ProfileSettings/AccountSection';
import { PrivacySection } from './ProfileSettings/PrivacySection';
import { EnhancedNotificationsSection } from './ProfileSettings/EnhancedNotificationsSection';
import { getDefaultWorkingHours, type WorkingHoursDay } from './ProfileSettings/WorkingHoursSection';
import { useProfileForm } from './ProfileSettings/hooks/useProfileForm';
import { useCategoryManagement } from './ProfileSettings/hooks/useCategoryManagement';
import { useProfileSave } from './ProfileSettings/hooks/useProfileSave';
import { useHybridSave } from './ProfileSettings/hooks/useHybridSave';
import { useLocalProfileData } from './ProfileSettings/hooks/useLocalProfileData';
import { UnsavedChangesDialog } from '@/src/shared/ui/UnsavedChangesDialog';
import { AutoSaveIndicator } from '@/src/shared/ui/AutoSaveIndicator';
import { SubscriptionCard } from '@/src/features/profile/components/my-worker/SubscriptionCard';
// DEPRECATED (Identity Verification disabled): hidden until identity verification is
// reintroduced. Only email verification (at registration) is active for now.
// import { VerificationSection } from '@/src/features/verification/components';
// import { useVerificationStatus, useStartVerification } from '@/src/features/verification/hooks';
// import { VerificationStatus as VerificationStatusEnum } from '@/src/features/verification/types';

export interface ProfileSettingsProps {
  onClose?: () => void;
}

type SettingSection = 'edit-profile' | 'account' | 'privacy' | 'notifications' | 'verification' | 'subscription';

const getSettingSections = (isContractor: boolean, t: (key: string) => string) => {
  const baseSections = [
    {
      id: 'edit-profile' as const,
      title: t('settingsSections.editProfile'),
    },
    {
      id: 'account' as const,
      title: t('settingsSections.account'),
    },
    {
      id: 'privacy' as const,
      title: t('settingsSections.privacy'),
    },
    {
      id: 'notifications' as const,
      title: t('settingsSections.notifications'),
    },
  ];

  // Add subscription section only for contractors
  // Identity Verification section disabled (see note at top of file)
  if (isContractor) {
    // baseSections.push({
    //   id: 'verification' as const,
    //   title: t('settingsSections.verification'),
    // });
    baseSections.push({
      id: 'subscription' as const,
      title: t('settingsSections.subscription'),
    });
  }

  return baseSections;
};

export function ProfileSettings({ onClose }: ProfileSettingsProps) {
  const [activeSection, setActiveSection] = useState<SettingSection>('edit-profile');
  const { user: currentUser, activeActor, _hasHydrated } = useAuthStore();
  const t = useTranslations('profile');

  // Identity Verification hooks disabled (see note at top of file)
  // const {
  //   data: verificationData,
  //   isLoading: isLoadingVerification,
  //   error: verificationError
  // } = useVerificationStatus({
  //   enabled: _hasHydrated && activeActor?.type === 'WORKER',
  // });
  // const { startVerification, isStarting } = useStartVerification();

  // Determine if user is in contractor mode (WORKER actor type)
  const isContractorMode = activeActor?.type === 'WORKER';

  // Get setting sections based on user type
  const settingSections = getSettingSections(isContractorMode, t);

  // Tab icons mapping (using available IconName types)
  const tabIcons: Record<SettingSection, 'edit' | 'person' | 'password' | 'bell' | 'claim' | 'star'> = {
    'edit-profile': 'edit',
    'account': 'person',
    'privacy': 'password',
    'notifications': 'bell',
    'verification': 'claim',
    'subscription': 'star',
  };

  // Fetch profile data using /contractors/me endpoint (only for contractors)
  const { data: profileData, isLoading: isLoadingContractor, isFetching, error } = useMyContractorProfile(
    _hasHydrated && !!currentUser && isContractorMode,
    false
  );

  // Fetch client profile data using /clients/me endpoint (only for clients)
  const { data: clientProfileData, isLoading: isLoadingClient, error: clientError } = useMyClientProfile(
    _hasHydrated && !!currentUser && !isContractorMode
  );

  // Combined loading state
  const isLoading = isContractorMode ? isLoadingContractor : isLoadingClient;

  // Fetch categories data (only for contractors)
  const { data: availableCategories } = useCategories(
    _hasHydrated && isContractorMode
  );
  const { data: myCategories } = useMyCategories(
    _hasHydrated && isContractorMode
  );

  // Fetch portfolio and services data (only for contractors)
  const { data: portfolioData } = useMyPortfolio(_hasHydrated && isContractorMode);
  const { data: myServices } = useMyServices(
    _hasHydrated && isContractorMode
  );

  // Mutations - Contractor
  const updateProfileMutation = useUpdateContractorProfile();
  const updateLocationMutation = useUpdateContractorLocation();
  const updateUserMutation = useUpdateUserProfile();
  const updateActorMutation = useUpdateActorProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const deletePortfolioMutation = useDeletePortfolioItem();
  const createPortfolioMutation = useCreatePortfolioItem();
  const assignCategoriesMutation = useAssignCategories();
  const createServiceMutation = useCreateService();
  const updateServiceMutation = useUpdateService();
  const deleteServiceMutation = useDeleteService();

  // Mutations - Client
  const updateClientProfileMutation = useUpdateClientProfile();
  const updateClientLocationMutation = useUpdateClientLocation();

  // Custom hooks for form management
  const {
    formData,
    isFormDataLoaded,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setAddress,
    setCity,
    setProvince,
    setPostalCode,
    setLatitude,
    setLongitude,
    setServiceArea,
    setShowLocation,
    setProfileDescription,
    setHourlyRate,
    setSelectedAvatar,
    setAvatarUrl,
    validationErrors,
    setValidationErrors,
    clearValidationError,
  } = useProfileForm({
    currentUser,
    profileData,
    clientProfileData,
    isContractorMode,
    isClientDataLoading: isLoadingClient,
  });

  // Custom hook for category management
  const {
    selectedCategoryGroups,
    rootCategories,
    getSubcategories,
    handleAddCategoryGroup,
    handleRemoveCategoryGroup,
    handleCategoryChange,
    handleSubcategoryToggle,
    handleSubcategoryPriceChange,
  } = useCategoryManagement({
    availableCategories,
    myCategories,
    myServices,
    isEnabled: _hasHydrated && isContractorMode,
  });

  // Working hours from API
  const { data: workingHoursData, isLoading: isLoadingWorkingHours } = useMyWorkingHours(_hasHydrated && isContractorMode);
  const updateWorkingHoursMutation = useUpdateWorkingHours();

  // Working hours state - initialized from API or defaults
  const [workingHours, setWorkingHours] = useState<WorkingHoursDay[]>(() => getDefaultWorkingHours());
  const [initialWorkingHours, setInitialWorkingHours] = useState<WorkingHoursDay[]>(() => getDefaultWorkingHours());
  const [workingHoursInitialized, setWorkingHoursInitialized] = useState(false);

  // Load working hours from API when data is available
  useEffect(() => {
    if (workingHoursInitialized) return;

    if (workingHoursData !== undefined) {
      if (workingHoursData.length > 0) {
        // Transform API data to component format
        const transformedHours: WorkingHoursDay[] = workingHoursData.map((wh) => ({
          dayOfWeek: wh.dayOfWeek,
          startTime: wh.startTime,
          endTime: wh.endTime,
          isActive: wh.isActive,
        }));
        setWorkingHours(transformedHours);
        setInitialWorkingHours(transformedHours);
      }
      // If empty array from API, keep defaults
      setWorkingHoursInitialized(true);
    }
  }, [workingHoursData, workingHoursInitialized, isLoadingWorkingHours]);

  // Local profile data hook for social media links and certificates (localStorage persistence)
  const {
    socialMediaLinks,
    setSocialMediaLinks,
    certificates,
    addCertificates,
    deleteCertificate,
    addSocialMediaLink,
  } = useLocalProfileData({
    userId: currentUser?.id,
    enabled: _hasHydrated && isContractorMode,
  });

  // Custom hook for save logic
  const { handleSave, saveStatus, isSaving, locationWarning, clearLocationWarning } = useProfileSave({
    currentUser,
    profileData,
    clientProfileData,
    isContractorMode,
    updateUser: updateUserMutation,
    updateActor: updateActorMutation,
    updateProfile: updateProfileMutation,
    updateLocation: updateLocationMutation,
    uploadAvatar: uploadAvatarMutation,
    assignCategories: assignCategoriesMutation,
    createService: createServiceMutation,
    updateService: updateServiceMutation,
    deleteService: deleteServiceMutation,
    myServices,
    availableCategories,
    // Client-specific mutations
    updateClientProfile: updateClientProfileMutation,
    updateClientLocation: updateClientLocationMutation,
    // On avatar upload success, update form state
    onAvatarSuccess: (newAvatarUrl) => {
      setAvatarUrl(newAvatarUrl);
      setSelectedAvatar(null);
    }
  });

  // Store initial form data for change detection
  const [initialFormData, setInitialFormData] = useState<typeof formData | null>(null);
  const [initialCategories, setInitialCategories] = useState(selectedCategoryGroups);

  // Check if categories are fully loaded (for contractor mode)
  // All three must be defined (not undefined) for selectedCategoryGroups to be populated
  const areCategoriesLoaded = !isContractorMode || (
    myCategories !== undefined &&
    myServices !== undefined &&
    availableCategories !== undefined
  );

  // Set initial values when profile loads (for both contractor and client modes)
  useEffect(() => {
    // For contractor mode - use profileData AND wait for categories AND form data to load
    if (isContractorMode && profileData && areCategoriesLoaded && isFormDataLoaded && !initialFormData) {
      setInitialFormData({ ...formData });
      setInitialCategories([...selectedCategoryGroups]);
    }
    // For client mode - use clientProfileData AND wait for form data to load
    if (!isContractorMode && clientProfileData && isFormDataLoaded && !initialFormData) {
      setInitialFormData({ ...formData });
      setInitialCategories([]);
    }
  }, [profileData, clientProfileData, formData, selectedCategoryGroups, initialFormData, isContractorMode, areCategoriesLoaded, isFormDataLoaded]);

  // Hybrid save hook for auto-save simple fields + unsaved changes warning
  const {
    autoSaveStatus,
    hasUnsavedCriticalChanges,
    isDialogOpen,
    closeDialog,
    saveAndContinue,
    discardAndContinue,
    markCriticalAsSaved,
  } = useHybridSave({
    formData,
    selectedCategoryGroups,
    workingHours,
    initialFormData,
    initialCategories,
    initialWorkingHours: initialWorkingHours || getDefaultWorkingHours(),
    onAutoSave: async (simpleData) => {
      // Auto-save simple fields via actor update
      const fullDisplayName = `${simpleData.firstName} ${simpleData.lastName}`.trim();
      const phoneToSend = simpleData.phone && simpleData.phone !== '+1' ? simpleData.phone : undefined;

      await updateActorMutation.mutateAsync({
        displayName: fullDisplayName,
        phone: phoneToSend,
      });

      // Update contractor profile fields
      if (isContractorMode && profileData) {
        const updates: Record<string, unknown> = {};
        if (simpleData.profileDescription !== (profileData.bio || '')) {
          updates.bio = simpleData.profileDescription;
        }
        if (simpleData.hourlyRate && parseFloat(simpleData.hourlyRate) !== profileData.hourlyRate) {
          updates.hourlyRate = parseFloat(simpleData.hourlyRate);
        }
        // @ts-ignore
        if (simpleData.showLocation !== (profileData.showLocation || false)) {
          updates.showLocation = simpleData.showLocation;
        }
        if (Object.keys(updates).length > 0) {
          await updateProfileMutation.mutateAsync(updates);
        }
      }

      // Update CLIENT profile fields (bio, showLocation)
      if (!isContractorMode && clientProfileData) {
        const clientUpdates: Record<string, unknown> = {};
        if (simpleData.profileDescription !== (clientProfileData.bio || '')) {
          clientUpdates.bio = simpleData.profileDescription;
        }
        if (simpleData.showLocation !== (clientProfileData.showLocation || false)) {
          clientUpdates.showLocation = simpleData.showLocation;
        }
        if (Object.keys(clientUpdates).length > 0) {
          await updateClientProfileMutation.mutateAsync(clientUpdates);
        }
      }

      // Save working hours if changed (contractor only)
      if (simpleData.workingHours && isContractorMode) {
        try {
          await updateWorkingHoursMutation.mutateAsync({
            workingHours: simpleData.workingHours,
          });
          // Update initial working hours after save
          setInitialWorkingHours(simpleData.workingHours);
        } catch (err) {
          console.error('[ProfileSettings] Failed to save working hours:', err);
          throw err;
        }
      }
    },
    onManualSave: async () => {
      await handleSave(formData, selectedCategoryGroups, setValidationErrors);
      markCriticalAsSaved();
    },
    // Enable hybrid save when:
    // - Page is hydrated
    // - User is logged in
    // - Initial form data is loaded
    // - For contractor: working hours are initialized
    // - For client: always ready (no working hours)
    enabled: _hasHydrated && !!currentUser && !!initialFormData && (isContractorMode ? workingHoursInitialized : true),
  });

  const handleSaveChanges = async () => {
    await handleSave(formData, selectedCategoryGroups, setValidationErrors);
    markCriticalAsSaved();
  };

  const handlePortfolioDelete = async (id: string) => {
    await deletePortfolioMutation.mutateAsync(id);
  };

  /**
   * Handle portfolio photo add with caption
   * Uploads the file to server storage and creates a portfolio item
   */
  const handlePortfolioPhotoAdd = async (photo: { file: File; caption: string }) => {
    try {
      // Upload image to server storage
      const uploadResult = await uploadGalleryImage(photo.file);

      // Create portfolio item with uploaded image URL
      await createPortfolioMutation.mutateAsync({
        title: photo.caption || photo.file.name.replace(/\.[^/.]+$/, ''),
        description: photo.caption || undefined,
        images: [uploadResult.url],
      });
    } catch (error) {
      console.error('[ProfileSettings] Failed to add portfolio photo:', error);
      throw error;
    }
  };

  const handleLocationChange = (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => {
    setAddress(location.address);
    setCity(location.city);
    setProvince(location.province);
    setPostalCode(location.postalCode);
    setLatitude(location.latitude);
    setLongitude(location.longitude);
  };

  const handleLocationSave = async (location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    latitude: number;
    longitude: number;
  }) => {
    // Update form state first
    handleLocationChange(location);

    // Save to server
    const serviceRadiusNum = parseFloat(formData.serviceArea.replace(/[^\d.]/g, ''));

    if (isContractorMode) {
      await updateLocationMutation.mutateAsync({
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address || undefined,
        city: location.city || undefined,
        province: location.province?.toUpperCase() || undefined,
        postalCode: location.postalCode || undefined,
        serviceRadius: serviceRadiusNum,
      });
    } else {
      await updateClientLocationMutation.mutateAsync({
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address || undefined,
        city: location.city || undefined,
        province: location.province?.toUpperCase() || undefined,
        postalCode: location.postalCode || undefined,
        serviceRadius: serviceRadiusNum,
      });
    }
  };

  const renderSectionContent = () => {
    // Show loading state while hydrating or loading data
    if (!_hasHydrated || isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      );
    }

    // Show error state
    if ((error && isContractorMode) || (clientError && !isContractorMode)) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Typography variant="h3" color="error">
            Failed to load profile
          </Typography>
          <Typography variant="body" color="secondary">
            Please try refreshing the page
          </Typography>
        </div>
      );
    }

    switch (activeSection) {
      case 'edit-profile':
        return isContractorMode ? (
          <ContractorProfileForm
            profileData={profileData}
            firstName={formData.firstName}
            lastName={formData.lastName}
            email={formData.email}
            phone={formData.phone}
            address={formData.address}
            city={formData.city}
            province={formData.province}
            postalCode={formData.postalCode}
            latitude={formData.latitude}
            longitude={formData.longitude}
            serviceArea={formData.serviceArea}
            showLocation={formData.showLocation}
            profileDescription={formData.profileDescription}
            hourlyRate={formData.hourlyRate}
            selectedAvatar={formData.selectedAvatar}
            avatarUrl={formData.avatarUrl}
            validationErrors={validationErrors}
            saveStatus={saveStatus}
            isSaving={isSaving}
            selectedCategoryGroups={selectedCategoryGroups}
            rootCategories={rootCategories}
            getSubcategories={getSubcategories}
            onAddCategory={handleAddCategoryGroup}
            onRemoveCategory={handleRemoveCategoryGroup}
            onCategoryChange={handleCategoryChange}
            onSubcategoryToggle={handleSubcategoryToggle}
            onSubcategoryPriceChange={handleSubcategoryPriceChange}
            portfolioData={portfolioData}
            onPortfolioDelete={handlePortfolioDelete}
            isPortfolioDeleting={deletePortfolioMutation.isPending}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
            onLocationChange={handleLocationChange}
            onLocationSave={handleLocationSave}
            onServiceAreaChange={setServiceArea}
            onShowLocationChange={setShowLocation}
            onProfileDescriptionChange={setProfileDescription}
            onHourlyRateChange={setHourlyRate}
            onAvatarChange={setSelectedAvatar}
            onSave={handleSaveChanges}
            onClearError={clearValidationError}
            workingHours={workingHours}
            onWorkingHoursChange={setWorkingHours}
            locationWarning={locationWarning}
            onDismissLocationWarning={clearLocationWarning}
            socialMediaLinks={socialMediaLinks}
            onSocialMediaLinksChange={setSocialMediaLinks}
            onAddSocialMedia={addSocialMediaLink}
            certificates={certificates}
            onAddCertificates={addCertificates}
            onDeleteCertificate={deleteCertificate}
            onPortfolioPhotoAdd={handlePortfolioPhotoAdd}
          />
        ) : (
          <ClientProfileForm
            currentAvatarUrl={formData.avatarUrl}
            firstName={formData.firstName}
            lastName={formData.lastName}
            email={formData.email}
            phone={formData.phone}
            address={formData.address}
            city={formData.city}
            province={formData.province}
            postalCode={formData.postalCode}
            latitude={formData.latitude}
            longitude={formData.longitude}
            serviceArea={formData.serviceArea}
            showLocation={formData.showLocation}
            profileDescription={formData.profileDescription}
            selectedAvatar={formData.selectedAvatar}
            validationErrors={validationErrors}
            saveStatus={saveStatus}
            isSaving={isSaving}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            onEmailChange={setEmail}
            onPhoneChange={setPhone}
            onLocationChange={handleLocationChange}
            onLocationSave={handleLocationSave}
            onLocationClear={() => {
              setAddress('');
              setCity('');
              setProvince('');
              setPostalCode('');
              setLatitude(null);
              setLongitude(null);
            }}
            onServiceAreaChange={setServiceArea}
            onShowLocationChange={setShowLocation}
            onProfileDescriptionChange={setProfileDescription}
            onAvatarChange={setSelectedAvatar}
            onSave={handleSaveChanges}
            onClearError={clearValidationError}
          />
        );
      case 'account':
        return <AccountSection />;
      case 'privacy':
        return <PrivacySection />;
      case 'notifications':
        return <EnhancedNotificationsSection />;
      // DEPRECATED (Identity Verification disabled): the 'verification' section is no
      // longer added to the nav, so this case is unreachable. Kept (commented) for when
      // identity verification is reintroduced.
      // case 'verification':
      //   if (!isContractorMode) return null;
      //
      //   // Show error message if contractor profile not found
      //   if (verificationError && verificationError.message.includes('contractor profile')) {
      //     return (
      //       <div className="space-y-4">
      //         <Typography variant="h3" className="mb-2">
      //           {t('settingsSections.verification')}
      //         </Typography>
      //         <div className="rounded-lg border border-warning/20 bg-warning/10 p-4">
      //           <div className="flex gap-3 items-start">
      //             <Icon name="warning" size="md" className="text-warning flex-shrink-0 mt-0.5" />
      //             <div className="flex-1">
      //               <Typography variant="bodyMd" className="font-medium mb-1">
      //                 Contractor Profile Required
      //               </Typography>
      //               <Typography variant="bodySm" color="secondary">
      //                 Please complete your contractor profile setup in the "Edit Profile" section first.
      //                 After saving your profile, refresh this page to access identity verification.
      //               </Typography>
      //             </div>
      //           </div>
      //         </div>
      //       </div>
      //     );
      //   }
      //
      //   return (
      //     <VerificationSection
      //       status={(verificationData?.status as VerificationStatusEnum) || VerificationStatusEnum.PENDING}
      //       verifiedAt={verificationData?.verifiedAt}
      //       isLoading={isLoadingVerification}
      //       isStarting={isStarting}
      //       onStartVerification={startVerification}
      //     />
      //   );
      case 'subscription':
        return isContractorMode ? <SubscriptionCard /> : null;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        isOpen={isDialogOpen}
        onSave={saveAndContinue}
        onDiscard={discardAndContinue}
        onCancel={closeDialog}
        isSaving={isSaving}
      />

      <div className="max-w-[1440px] mx-auto px-8 py-8">
        {/* Layout with Sidebar */}
        <div className="flex gap-8">
          {/* Sidebar with Vertical Tabs */}
          <aside className="w-64 flex-shrink-0">
            <div className="sticky top-8 space-y-1">
              {settingSections.map((section) => {
                const isActive = activeSection === section.id;
                const iconName = tabIcons[section.id];
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors text-left ${
                      isActive
                        ? 'bg-accent-primary/10 text-accent-primary font-semibold'
                        : 'text-text-secondary hover:bg-background-secondary hover:text-text-primary'
                    }`}
                  >
                    <Icon
                      name={iconName}
                      size="sm"
                      color={isActive ? 'accent' : 'secondary'}
                    />
                    <Typography
                      variant="body"
                      weight={isActive ? 'semibold' : 'regular'}
                      className={isActive ? 'text-accent-primary' : ''}
                    >
                      {section.title}
                    </Typography>
                  </button>
                );
              })}

              {/* Auto-save and unsaved changes indicators */}
              {activeSection === 'edit-profile' && (
                <div className="mt-4 px-4 space-y-2">
                  {/* Auto-save status indicator */}
                  <AutoSaveIndicator
                    status={hasUnsavedCriticalChanges && autoSaveStatus === 'pending' ? 'idle' : autoSaveStatus}
                  />
                  {/* Unsaved critical changes indicator */}
                  {hasUnsavedCriticalChanges && (
                    <span className="text-xs text-feedback-warning bg-feedback-warning/10 px-2 py-1 rounded inline-block">
                      Unsaved changes
                    </span>
                  )}
                </div>
              )}
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0">
            <div className="bg-white">
              {renderSectionContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
